import type { UseFormReturn } from 'react-hook-form';

import { useEffect } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { UserLanguage } from '~/api/user/types';

import { Field, Input, Select } from '~/components/Form';
import { MainOccupation } from '~/constants/constants';
import { useLanguageContext } from '~/providers/LanguageProvider/LanguageProvider.context';
import { noop } from '~/utils/functions';
import { parsePhoneNumber } from '~/utils/libphonenumberjs';
import { isNotBlank } from '~/utils/string';

import type { PersonalInfoFormType } from './PersonalInfo.types';

import { columnsStyle } from '../../components/SettingsSection.css';

const languages = ['nl', 'fr'] as const satisfies UserLanguage[];
const mainOccupations = [MainOccupation.yes, MainOccupation.no] as const;

export const PersonalInfo = ({ form }: { form: UseFormReturn<PersonalInfoFormType> }) => {
  const { setPreviewLanguage } = useLanguageContext();

  const { control } = form;
  const { t } = useTranslation(['common', 'settings', 'validation']);

  const selectedLanguage = useWatch({ control, name: 'lang' });

  useEffect(() => {
    setPreviewLanguage(selectedLanguage);
    return () => setPreviewLanguage(null);
  }, [selectedLanguage, setPreviewLanguage]);

  const languageItems = languages.map((language) => ({ label: t(`common:languages.${language}`), value: language }));

  const mainOccupationItems = mainOccupations.map((mainOccupation) => ({
    label: t(`settings:fields.mainOccupation.options.${mainOccupation}`),
    value: mainOccupation,
  }));

  return (
    <div className={columnsStyle}>
      <Controller
        control={control}
        name="firstName"
        render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid } }) => (
          <Field.Root invalid={invalid} name={name}>
            <Field.Label>{t('settings:fields.firstName.label')}</Field.Label>

            <Input
              onBlur={onBlur}
              onValueChange={onChange}
              placeholder={t('settings:fields.firstName.placeholder')}
              ref={ref}
              value={value}
            />

            <Field.Error match={!!error}>{error?.message}</Field.Error>
          </Field.Root>
        )}
        rules={{
          required: { value: true, message: t('validation:required') },
          validate: { notBlank: (value) => isNotBlank(value) || t('validation:required') },
          maxLength: { value: 255, message: t('validation:maxLength', { attribute: t('settings:fields.firstName.label'), max: 255 }) },
        }}
      />

      <Controller
        control={control}
        name="lastName"
        render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid } }) => (
          <Field.Root invalid={invalid} name={name}>
            <Field.Label>{t('settings:fields.lastName.label')}</Field.Label>

            <Input
              onBlur={onBlur}
              onValueChange={onChange}
              placeholder={t('settings:fields.lastName.placeholder')}
              ref={ref}
              value={value}
            />

            <Field.Error match={!!error}>{error?.message}</Field.Error>
          </Field.Root>
        )}
        rules={{
          required: { value: true, message: t('validation:required') },
          validate: { notBlank: (value) => isNotBlank(value) || t('validation:required') },
          maxLength: { value: 255, message: t('validation:maxLength', { attribute: t('settings:fields.lastName.label'), max: 255 }) },
        }}
      />

      <Controller
        control={control}
        name="lang"
        render={({ field: { name, onChange, value }, fieldState: { error, invalid } }) => (
          <Field.Root invalid={invalid} name={name}>
            <Field.Label>{t('settings:fields.lang.label')}</Field.Label>

            <Select.Root items={languageItems} onValueChange={onChange} value={value}>
              <Select.Button />

              <Select.List>
                {languageItems.map((item) => (
                  <Select.Item key={item.value} value={item.value}>
                    {item.label}
                  </Select.Item>
                ))}
              </Select.List>
            </Select.Root>

            <Field.Error match={!!error}>{error?.message}</Field.Error>
          </Field.Root>
        )}
        rules={{ required: { value: true, message: t('validation:required') } }}
      />

      <Controller
        control={control}
        name="mainOccupation"
        render={({ field: { name, onChange, value }, fieldState: { error, invalid } }) => (
          <Field.Root invalid={invalid} name={name}>
            <Field.Label>{t('settings:fields.mainOccupation.label')}</Field.Label>

            <Select.Root items={mainOccupationItems} onValueChange={onChange} value={value}>
              <Select.Button />

              <Select.List>
                {mainOccupationItems.map((item) => (
                  <Select.Item key={item.value} value={item.value}>
                    {item.label}
                  </Select.Item>
                ))}
              </Select.List>
            </Select.Root>

            <Field.Error match={!!error}>{error?.message}</Field.Error>
          </Field.Root>
        )}
        rules={{ required: { value: true, message: t('validation:required') } }}
      />

      <Controller
        control={control}
        name="email"
        render={({ field: { name, value } }) => (
          <Field.Root disabled name={name}>
            <Field.Label>{t('settings:fields.email.label')}</Field.Label>
            <Input onValueChange={noop} value={value} />
          </Field.Root>
        )}
      />

      <Controller
        control={control}
        name="phone"
        render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid } }) => (
          <Field.Root invalid={invalid} name={name}>
            <Field.Label>{t('settings:fields.phone.label')}</Field.Label>

            <Input
              onBlur={onBlur}
              onValueChange={onChange}
              placeholder={t('settings:fields.phone.placeholder')}
              ref={ref}
              type="tel"
              value={value}
            />

            <Field.Error match={!!error}>{error?.message}</Field.Error>
          </Field.Root>
        )}
        rules={{
          required: { value: true, message: t('validation:required') },
          maxLength: { value: 255, message: t('validation:maxLength', { attribute: t('settings:fields.phone.label'), max: 255 }) },
          validate: {
            notBlank: (value) => isNotBlank(value) || t('validation:required'),
            missingCallingCode: (value) => value.trim().startsWith('+') || t('validation:phoneNumber.missingCallingCode'),
            format: (value) => !!parsePhoneNumber(value)?.isValid() || t('validation:phoneNumber.format'),
          },
        }}
      />
    </div>
  );
};
